import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, Lock, CheckCircle } from "lucide-react";
import { BASE_URL, trim } from "../../../utils/helpers";

type FormErrors = {
  password?: string;
  password_confirmation?: string;
  server?: string;
};

const ResetPasswordForm = () => {
  const { search } = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(search);
  const emailFromUrl = params.get("email");
  const tokenFromUrl = params.get("token");

  const [email, setEmail] = useState(emailFromUrl || "");
  const [token] = useState(tokenFromUrl || "");

  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // If no token or email → redirect
  useEffect(() => {
    if (!emailFromUrl || !tokenFromUrl) {
      navigate("/");
    }
  }, [emailFromUrl, tokenFromUrl, navigate]);

  const validate = (): FormErrors => {
    const newErrors: FormErrors = {};

    if (!password) newErrors.password = "Password is required";
    else if (password.length < 6)
      newErrors.password = "Password must be at least 6 characters";

    if (!passwordConfirmation)
      newErrors.password_confirmation = "Confirm your password";
    else if (password !== passwordConfirmation)
      newErrors.password_confirmation = "Passwords do not match";

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    try {
      const response = await fetch(`${BASE_URL}reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trim(email),
          token,
          password,
          password_confirmation: passwordConfirmation,
        }),
      });

      if (response.status === 200) {
        setIsSuccess(true);

        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        const data = await response.json();
        setErrors({ server: data?.message || "Failed to reset password." });
      }
    } catch (error) {
      setErrors({ server: "Something went wrong. Try again later." });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <section className="flex items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md bg-white rounded-2xl p-6 lg:p-8 border shadow-sm text-center space-y-4">
          <div className="flex justify-center">
            <div className="rounded-full bg-green-100 p-3">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold">Password Reset Successful</h2>
          <p className="text-gray-600">Redirecting to login page...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl p-6 lg:p-8 border shadow-sm space-y-6">
        <h2 className="text-2xl font-bold text-center">Reset Password</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email (read-only) */}
          <div>
            <label className="text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              value={email}
              readOnly
              className="block w-full mt-1 py-2.5 px-3 border rounded-lg bg-gray-100 cursor-not-allowed text-gray-600"
            />
          </div>

          {/* Password */}
          <div>
            <label className="text-sm font-medium text-gray-700">
              New Password
            </label>
            <div className="relative">
              <Lock className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
              <input
                type="password"
                className={`w-full pl-10 py-2.5 border rounded-lg ${
                  errors.password ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {errors.password && (
              <p className="text-red-600 text-sm">{errors.password}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="text-sm font-medium text-gray-700">
              Confirm Password
            </label>
            <input
              type="password"
              className={`w-full py-2.5 px-3 border rounded-lg ${
                errors.password_confirmation
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
              placeholder="••••••••"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
            />
            {errors.password_confirmation && (
              <p className="text-red-600 text-sm">
                {errors.password_confirmation}
              </p>
            )}
          </div>

          {/* Server Error */}
          {errors.server && (
            <p className="text-red-600 text-center text-sm">{errors.server}</p>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-orange-500 text-white font-medium py-2.5 rounded-lg flex justify-center items-center space-x-2 disabled:opacity-60"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Updating…</span>
              </>
            ) : (
              "Reset Password"
            )}
          </button>
        </form>
      </div>
    </section>
  );
};

export default ResetPasswordForm;
